import { API, Auth } from "aws-amplify"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"

const fetchOrganizationInvoices = async organizationId => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
  }
  const res = await API.get("CORE", `/invoices/${organizationId}`, payload)
  return res
}

export const useOrganizationInvoices = (organizationId, enabled = false) => {
  return useQuery({
    queryKey: ["organizationInvoices", organizationId],
    queryFn: async () => {
      const res = await fetchOrganizationInvoices(organizationId)
      if (Array.isArray(res)) {
        return res.filter(inv => !inv._deleted_at)
      }
      if (res && typeof res === "object" && !res._deleted_at) {
        return res
      }
      return []
    },
    enabled,
    refetchInterval: data => {
      const list = Array.isArray(data) ? data : []
      const hasPending = list.some(inv =>
        INVOPOP_PENDING_STATUSES.has(inv.invopopData?.invopopStatus),
      )
      return hasPending ? 5000 : false
    },
  })
}

const fetchCreateInvoice = async (organizationId, invoice) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
    body: invoice,
  }

  const res = await API.post(
    "CORE",
    `/invoices/?organizationId=${organizationId}`,
    payload,
  )
  return res
}

export const useCreateInvoice = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, invoice }) =>
      fetchCreateInvoice(organizationId, invoice),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizationInvoices"] })
    },
  })
}

const fetchIssueInvoice = async (organizationId, invoice, isOldInvoice) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
  }
  const query = isOldInvoice ? "?isOldInvoice=true" : ""
  const res = await API.put(
    "CORE",
    `/invoices/issue/${organizationId}/${invoice.invoiceId}${query}`,
    payload,
  )
  return res
}

export const useIssueInvoice = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, invoice, isOldInvoice }) =>
      fetchIssueInvoice(organizationId, invoice, isOldInvoice),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizationInvoices"] })
      queryClient.invalidateQueries({ queryKey: ["projectInvoices"] })
      queryClient.invalidateQueries({ queryKey: ["invoice"] })
      queryClient.invalidateQueries({ queryKey: ["series"] })
    },
  })
}

const fetchEditInvoice = async (organizationId, invoice) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
    body: invoice,
  }
  const res = await API.put(
    "CORE",
    `/invoices/?organizationId=${organizationId}`,
    payload,
  )
  return res
}

export const useEditInvoice = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, invoice }) =>
      fetchEditInvoice(organizationId, invoice),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizationInvoices"] })
      queryClient.invalidateQueries({ queryKey: ["projectInvoices"] })
      queryClient.invalidateQueries({ queryKey: ["invoice"] })
    },
  })
}

const fetchDeleteInvoice = async (organizationId, invoiceId) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
  }
  const res = await API.del(
    "CORE",
    `/invoices/${organizationId}/${invoiceId}`,
    payload,
  )
  return res
}

export const useDeleteInvoice = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, invoiceId }) =>
      fetchDeleteInvoice(organizationId, invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizationInvoices"] })
      queryClient.invalidateQueries({ queryKey: ["projectInvoices"] })
      queryClient.invalidateQueries({ queryKey: ["invoice"] })
    },
  })
}

const fetchSingleInvoice = async (organizationId, invoiceId) => {
  console.log("fetchSingleInvoice", organizationId, invoiceId)
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
  }
  const res = await API.get(
    "CORE",
    `/invoices/${organizationId}/${invoiceId}`,
    payload,
  )
  return res
}

const INVOPOP_PENDING_STATUSES = new Set(["pending", "processing"])

export const useSingleInvoice = (organizationId, invoiceId, enabled = true) => {
  return useQuery({
    queryKey: ["invoice", organizationId, invoiceId],
    queryFn: () => fetchSingleInvoice(organizationId, invoiceId),
    enabled: !!organizationId && !!invoiceId && enabled,
    refetchInterval: data => {
      const status = data?.invopopData?.invopopStatus
      return INVOPOP_PENDING_STATUSES.has(status) ? 5000 : false
    },
  })
}

const fetchProjectInvoices = async (organizationId, projectId) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
  }
  const res = await API.get(
    "CORE",
    `/invoices/${organizationId}/project/${projectId}`,
    payload,
  )
  return res
}

export const useProjectInvoices = (
  organizationId,
  projectId,
  enabled = true,
) => {
  return useQuery({
    queryKey: ["projectInvoices", organizationId, projectId],
    queryFn: () => fetchProjectInvoices(organizationId, projectId),
    enabled: !!organizationId && !!projectId && enabled,
  })
}

const fetchCancelInvoice = async (organizationId, invoiceId) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
  }
  const res = await API.put(
    "CORE",
    `/invoices/cancel/${organizationId}/${invoiceId}`,
    payload,
  )
  return res
}

export const useCancelInvoice = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, invoiceId }) =>
      fetchCancelInvoice(organizationId, invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizationInvoices"] })
      queryClient.invalidateQueries({ queryKey: ["projectInvoices"] })
      queryClient.invalidateQueries({ queryKey: ["invoice"] })
    },
  })
}

// Reintento de envío, no reemisión: la factura ya tiene número y no se renumera. `issue`
// aborta con 409 en una factura emitida, así que el reintento va por este endpoint, que
// reusa el silo existente y reenvía a Verifactu.
const fetchResendToInvopop = async (organizationId, invoiceId) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
    body: { organizationId, invoiceId },
  }
  const res = await API.post(
    "CORE",
    "/invopop/send_to_invopop/?workflow=verifactu",
    payload,
  )
  return res
}

export const useResendToInvopop = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, invoiceId }) =>
      fetchResendToInvopop(organizationId, invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizationInvoices"] })
      queryClient.invalidateQueries({ queryKey: ["projectInvoices"] })
      queryClient.invalidateQueries({ queryKey: ["invoice"] })
    },
  })
}

const fetchUpdatePaymentStatus = async (
  organizationId,
  invoiceId,
  paymentStatus,
) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
  }
  const res = await API.patch(
    "CORE",
    `/invoices/payment-status/${organizationId}/${invoiceId}?payment_status=${paymentStatus}`,
    payload,
  )
  return res
}

export const useUpdatePaymentStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, invoice }) =>
      fetchUpdatePaymentStatus(
        organizationId,
        invoice.invoiceId,
        invoice.paymentStatus,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizationInvoices"] })
    },
  })
}
